import { TDR } from 'tdr-common';

/**
 * Base checkout configuration with policies, details, payment and confirmation steps
 */
const baseConfig = {
	policies: {
		order: 1,
		path: 'policies',
		titleKey: 'policies',
		prev: null,
		next: 'details'
	},
	details: {
		order: 2,
		path: 'details',
		titleKey: 'your_details',
		prev: 'policies',
		next: 'payment'
	},
	payment: {
		order: 3,
		path: 'payment',
		titleKey: 'payment',
		prev: 'details',
		next: 'confirmation'
	},
	confirmation: {
		order: 4,
		path: 'confirmation',
		titleKey: 'confirmation',
		prev: 'payment',
		next: null
	}
};

export interface CheckoutNavConfig {
	[key: string]: {
		order: number;
		titleKey?: string;
		title?: string;
		path: string;
		prev?: string | null;
		next?: string;
		isEcommerce?: boolean;
		validation?: TDR.EcommerceValidationConfig;
		sectionData?: TDR.Ecommerce.EcommerceSection;
	}
}

/**
 * Predefined ecommerce route keys (max 3 sections)
 */
const ECOMMERCE_ROUTES = ['ecommerce-1', 'ecommerce-2', 'ecommerce-3'] as const;

/**
 * Dynamically generates the checkout navigation configuration based on ecommerce sections
 * Uses predefined routes: /ecommerce-1, /ecommerce-2, /ecommerce-3
 *
 * @param ecommerceSections Array of ecommerce sections from checkout session (max 3)
 * @returns The appropriate navigation configuration object
 */
export function getEcommerceCheckoutNavConfig(ecommerceSections: TDR.Ecommerce.EcommerceSection[] = []): typeof baseConfig {
	// If no ecommerce sections, return the base config as-is
	if (!ecommerceSections || ecommerceSections.length === 0) {
		return JSON.parse(JSON.stringify(baseConfig));
	}

	// Start with a copy of the base config
	const config = JSON.parse(JSON.stringify(baseConfig));

	// Sort sections by checkout position and limit to 3
	const sortedSections = [...ecommerceSections]
		.sort((a, b) => a.checkoutPosition - b.checkoutPosition)
		.slice(0, 3)
		.filter(section => section.title); // Remove items length check to allow empty sections

	// Create a map of all steps with their positions
	const allSteps = new Map<number, { type: 'ecommerce' | 'standard', data?: any, key: string }>();

	// Add standard steps at their default positions
	allSteps.set(5, { type: 'standard', key: 'policies' });
	allSteps.set(10, { type: 'standard', key: 'details' });
	allSteps.set(15, { type: 'standard', key: 'payment' });
	allSteps.set(20, { type: 'standard', key: 'confirmation' });

	// Add ecommerce sections at their configured positions
	for (let i = 0; i < sortedSections.length && i < 3; i++) {
		const section = sortedSections[i];
		const routeKey = ECOMMERCE_ROUTES[i];


		allSteps.set(section.checkoutPosition, {
			type: 'ecommerce',
			data: section,
			key: routeKey
		});
	}

	// Sort all steps by position
	const sortedSteps = Array.from(allSteps.entries())
		.sort(([posA], [posB]) => posA - posB);


	// Build the final config with proper ordering and navigation
	let previousStep: string | null = null;

	for (let i = 0; i < sortedSteps.length; i++) {
		const [, step] = sortedSteps[i];
		const isLast = i === sortedSteps.length - 1;
		const nextStep = isLast ? null : sortedSteps[i + 1][1].key;

		if (step.type === 'ecommerce') {
			config[step.key] = {
				order: i + 1,
				path: step.key,
				title: step.data.title,
				prev: previousStep,
				next: nextStep,
				isEcommerce: true,
				validation: step.data.validation,
				sectionData: step.data
			};
		}
		else {
			// Update standard step with proper ordering and navigation
			config[step.key] = {
				...config[step.key],
				order: i + 1,
				prev: previousStep,
				next: nextStep
			};
		}

		previousStep = step.key;
	}

	return config;
}

export function getInitialStep(navConfig: CheckoutNavConfig): string {
	return Object.values(navConfig).find(step => step.order === 1)?.path || 'policies';
}

export function getEcommerceSteps(navConfig: CheckoutNavConfig): string[] {
	return Object.keys(navConfig).filter(key =>
		navConfig[key].isEcommerce === true
	);
}